import WordSummaryView, {DisplayOptions, HeaderDisplayOptions} from "./WordSummaryView";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import BorderedSection from "./BorderedSection";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import {
    alpha,
    Box,
    Collapse,
    IconButton,
    InputAdornment,
    Stack,
    TextField,
    Theme
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import UndoIcon from "@mui/icons-material/Undo";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import TranslateIcon from "@mui/icons-material/Translate";
import HardwareIcon from "@mui/icons-material/Hardware";
import InfoIcon from "@mui/icons-material/Info";
import React, {forwardRef} from "react";
import ShuffleIcon from "@mui/icons-material/Shuffle";
import ThumbDownOffAltIcon from "@mui/icons-material/ThumbDownOffAlt";
import {WordWithSound} from "../../../common/types/words";
import {useTheme} from "@mui/material/styles";

export interface WordStudyItemViewProps {
    currentWord: WordWithSound | null;
    currentTaskType: string | null;
    language?: string;
    userInput: string;
    onUserInputChanged: (value: string) => void;
    answerSubmitted: boolean;
    isCorrectAnswer: boolean;
    submittedAnswerHasTypo: boolean;
    submitAnswer: () => void;
    nextItem: () => void;
    undo: () => void;
    reshuffle: () => void;
    handleIntentionalFail: () => void;
    toggleRevealAnswer: () => void;
    revealCorrectAnswer: boolean;
}

const getGradientBackground = (theme: Theme, light: string, main: string) =>
    `linear-gradient(135deg, ${light} 0%, ${theme.palette.common.white} 50%, ${main} 100%)`;

const getInputBackground = (theme: Theme, answerSubmitted: boolean, isCorrectAnswer: boolean, submittedAnswerHasTypo: boolean, currentTaskType: string) => {
    if (!answerSubmitted)
        return currentTaskType === "translation" ? alpha(theme.palette.primary.light, 0.5) : alpha(theme.palette.secondary.light, 0.5);
    if (submittedAnswerHasTypo)
        return getGradientBackground(theme, theme.palette.warning.light, theme.palette.warning.main);
    return isCorrectAnswer
        ? getGradientBackground(theme, theme.palette.success.light, theme.palette.success.main)
        : getGradientBackground(theme, theme.palette.error.light, theme.palette.error.main);
}

const getInputBorderColor = (theme: Theme, answerSubmitted: boolean, isCorrectAnswer: boolean, submittedAnswerHasTypo: boolean, currentTaskType: string) => {
    if (!answerSubmitted)
        return currentTaskType === "translation" ? theme.palette.primary.light : theme.palette.secondary.light;
    if (submittedAnswerHasTypo)
        return theme.palette.warning.main;
    return isCorrectAnswer ? theme.palette.success.main : theme.palette.error.main;
}

const WordStudyItemView = forwardRef<HTMLInputElement, WordStudyItemViewProps>(({
                               currentWord,
                               currentTaskType,
                               language,
                               userInput,
                               onUserInputChanged,
                               answerSubmitted,
                               isCorrectAnswer,
                               submittedAnswerHasTypo,
                               submitAnswer,
                               nextItem,
                               undo,
                               reshuffle,
                               handleIntentionalFail,
                               toggleRevealAnswer,
                               revealCorrectAnswer
}, ref) => {

    const theme = useTheme();

    const hoverBorder = getInputBorderColor(theme, answerSubmitted, isCorrectAnswer, submittedAnswerHasTypo, currentTaskType ?? "translation");

    return (
        <>
            {questionView()}
            {inputSectionView()}
            {informationView()}
        </>
    )

    function questionView() {
        const sharedDisplayOptions = {
            icon: LightbulbIcon,
            titleRearView: questionActionButtonsView(),
        }
        const lemmaHeaderDisplayOptions: DisplayOptions = {
            ...sharedDisplayOptions,
            title: language ?? "Foreign word",
            bgcolor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,

        };
        const translationHeaderDisplayOptions: DisplayOptions = {
            ...sharedDisplayOptions,
            title: "English",
            bgcolor: theme.palette.secondary.main,
            color: theme.palette.secondary.contrastText,
        };
        const headerDisplayOptions = currentTaskType === "translation" ?
            {lemma: lemmaHeaderDisplayOptions} : {translation: translationHeaderDisplayOptions};

        return (
            <WordSummaryView word={currentWord}
                             displayOptions={headerDisplayOptions}
                             animationDirection={1}
            />
        )
    }

    function questionActionButtonsView() {
        return (
            <Stack direction="row">
                <IconButton onClick={reshuffle} title={"Reshuffle (Tab)"} disabled={answerSubmitted} size={"small"}>
                    <ShuffleIcon/>
                </IconButton>
                <IconButton onClick={handleIntentionalFail} title={"Fail (Double Enter)"} disabled={answerSubmitted} size={"small"}>
                    <ThumbDownOffAltIcon/>
                </IconButton>
            </Stack>
        )
    }

    function inputSectionView() {
        return (
            <BorderedSection icon={TaskAltIcon}
                             title={"Your answer"}
                             marginTop={3}
                             sx={{
                                 p: 0,
                                 borderRadius: 4,
                                 position: "relative",
                                 borderWidth: 0
                            }}
            >
                {inputView()}
                {revealAnswerButtonView()}
            </BorderedSection>
        )
    }

    function inputView() {
        return (
            <TextField variant="outlined"
                       type="text"
                       fullWidth={true}
                       spellCheck={false}
                       autoCapitalize="off"
                       autoComplete="off"
                       autoFocus={true}
                       value={userInput}
                       onChange={e => onUserInputChanged(e.target.value)}
                       placeholder={currentTaskType === "translation" ? "English" : (language ?? "Foreign word")}
                       inputRef={ref}
                       sx={{
                           '& .MuiOutlinedInput-root': {
                               background: getInputBackground(theme, answerSubmitted, isCorrectAnswer, submittedAnswerHasTypo, currentTaskType ?? "translation"),
                               borderRadius: 3,
                               minHeight: 75,
                               '& input': {
                                   borderRadius: 12,
                                   fontSize: 20,
                                   fontWeight: 500
                               },
                               '& .MuiOutlinedInput-notchedOutline': {
                                   borderColor: "transparent",
                                   borderRadius: 3,
                                   borderWidth: 0.5,
                               },
                               '&:hover:not(.Mui-disabled) .MuiOutlinedInput-notchedOutline': {
                                   borderColor: hoverBorder
                               },

                               '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                   borderColor: "transparent"
                               },
                           },
                       }}
                       inputProps={{style: {textAlign: 'center'}}}
                       InputProps={{
                           endAdornment: (
                               <InputAdornment position="end">
                                   {!answerSubmitted && (
                                       <IconButton onClick={submitAnswer} title="Submit answer (Enter)" color={"inherit"} disabled={userInput.length === 0}>
                                           <SendIcon/>
                                       </IconButton>
                                   )}
                                   {answerSubmitted && (
                                       <IconButton onClick={nextItem} title="Next word (Enter)" color={"inherit"}>
                                           <NavigateNextIcon/>
                                       </IconButton>
                                   )}
                               </InputAdornment>
                           ),
                           startAdornment: (
                               <InputAdornment position="start" sx={{ visibility: answerSubmitted ? "visible" : "hidden"}}>
                                   <IconButton title="Undo (Backspace)" color={"inherit"} onClick={undo}>
                                       <UndoIcon/>
                                   </IconButton>
                               </InputAdornment>
                           ),
                       }}
            />
        )
    }

    function revealAnswerButtonView() {
        return answerSubmitted && (
            <IconButton
                onClick={toggleRevealAnswer}
                sx={{
                    position: "absolute",
                    bottom: -18,
                    left: "50%",
                    transform: `translateX(-50%) rotate(${revealCorrectAnswer ? 180 : 0}deg)`,
                    transition: "transform 200ms ease",
                    bgcolor: "background.paper",
                    boxShadow: 2,
                    "&:hover": {
                        bgcolor: "grey.100"
                    }
                }}
            >
                <ExpandMoreIcon />
            </IconButton>
        )
    }

    function informationView() {
        const sharedProps: HeaderDisplayOptions = {
            order: 1,
            title: "Correct answer",
            icon: TranslateIcon,
            bgcolor: theme.palette.primary.light,
            color: theme.palette.primary.contrastText,
            showImage: true,
            showPos: true
        }
        return (
            <Collapse in={revealCorrectAnswer} timeout="auto">
                <Box
                    sx={{
                        mt: 3,
                        p: 2,
                        bgcolor: 'grey.200',
                        borderRadius: 2,
                        boxShadow: 1
                    }}
                >
                    <WordSummaryView word={currentWord}
                                     displayOptions={{
                                         ...(currentTaskType === "translation" ? {translationHeader: sharedProps} : {lemmaHeader: sharedProps}),
                                         exampleSentence: {order: 2, title: "Example sentence", icon: InfoIcon, addMarginAbove: true, bgcolor: theme.palette.primary.light, color: theme.palette.primary.contrastText},
                                         conjugation: {order: 3, title: "Conjugation", icon: HardwareIcon, addMarginAbove: true, bgcolor: theme.palette.primary.light, color: theme.palette.primary.contrastText},
                                     }}
                                     animationDirection={1}
                    />
                </Box>
            </Collapse>
        )
    }

});

export default WordStudyItemView;